import React, { useState, useEffect } from 'react';
import { collection, query, limit, startAfter, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
//import { Spinner } from '../components/common/Spinner';
import { toast } from 'react-hot-toast';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastVisible, setLastVisible] = useState(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [availableDomains, setAvailableDomains] = useState([]);
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [editingOrg, setEditingOrg] = useState(null);
  const [newOrgId, setNewOrgId] = useState('');
  const [allUsers, setAllUsers] = useState([]); // Store all fetched users
  const [totalPages, setTotalPages] = useState(1);
  
  const USERS_PER_PAGE = 10;

  // Fetch all users on initial load
  const fetchAllUsers = async () => {
    try {
      setLoading(true);
      
      // Fetch a large batch of users (adjust limit as needed for your use case)
      let usersRef = collection(db, 'users');
      let usersQuery = query(usersRef, limit(100)); // Fetch more users to handle filtering
      
      const snapshot = await getDocs(usersQuery);
      
      if (snapshot.empty) {
        setUsers([]);
        setAllUsers([]);
        return;
      }
      
      const userData = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));
      
      // Extract all unique domains for filtering and sort them alphabetically
      const domains = [...new Set(userData.map(user => {
        const emailParts = user.email?.split('@');
        return emailParts?.length > 1 ? emailParts[1] : null;
      }).filter(Boolean))].sort();
      
      setAvailableDomains(domains);
      
      // Only auto-select all domains on the very first load
      if (selectedDomains.length === 0) {
        setSelectedDomains(domains);
      }
      
      setAllUsers(userData);
      
      // Apply initial filtering and pagination
      applyFiltersAndPagination(userData);
      
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  
  // Apply filters and pagination to the full dataset
  const applyFiltersAndPagination = (sourceData = allUsers) => {
    // Filter by search term and domain
    const filteredData = sourceData.filter(user => {
      // Filter by search term if provided
      const matchesSearch = !searchTerm || 
        user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filter by domain
      const emailParts = user.email?.split('@');
      const domain = emailParts?.length > 1 ? emailParts[1] : null;
      const matchesDomain = domain && selectedDomains.includes(domain);
      
      return matchesSearch && matchesDomain;
    });
    
    // Calculate total pages based on filtered data
    const calculatedTotalPages = Math.max(1, Math.ceil(filteredData.length / USERS_PER_PAGE));
    setTotalPages(calculatedTotalPages);
    
    // Reset to page 1 if current page is out of bounds
    if (page > calculatedTotalPages) {
      setPage(1);
    }
    
    // Slice the data for current page
    const startIndex = (page - 1) * USERS_PER_PAGE;
    const endIndex = startIndex + USERS_PER_PAGE;
    const paginatedData = filteredData.slice(startIndex, endIndex);
    
    setUsers(paginatedData);
  };

  // Initial fetch on component mount
  useEffect(() => {
    fetchAllUsers();
  }, []);
  
  // Reapply filters and pagination when filters or page changes
  useEffect(() => {
    applyFiltersAndPagination();
  }, [searchTerm, selectedDomains, page]);

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(prev => prev - 1);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1); // Reset to first page when searching
  };

  const handleDomainToggle = (domain) => {
    if (selectedDomains.includes(domain)) {
      setSelectedDomains(selectedDomains.filter(d => d !== domain));
    } else {
      setSelectedDomains([...selectedDomains, domain]);
    }
    setPage(1); // Reset to first page when changing filters
  };

  const handleSelectAllDomains = () => {
    if (selectedDomains.length === availableDomains.length) {
      setSelectedDomains([]);
    } else {
      setSelectedDomains([...availableDomains]);
    }
    setPage(1); // Reset to first page when changing filters
  };

  const startEditingOrg = (userId) => {
    setEditingOrg(userId);
    const user = users.find(u => u.uid === userId);
    setNewOrgId('');
  };

  const handleAddOrg = async (userId) => {
    if (!newOrgId.trim() || isNaN(parseInt(newOrgId))) {
      toast.error('Please enter a valid numeric organization ID');
      return;
    }
    
    try {
      const user = users.find(u => u.uid === userId);
      const numericOrgId = parseInt(newOrgId);
      
      // If user has no org array yet, create one
      const updatedOrgs = user.org 
        ? [...user.org, numericOrgId] 
        : [numericOrgId];
      
      // Update in Firestore
      await updateDoc(doc(db, 'users', userId), {
        org: updatedOrgs
      });
      
      // Update local state
      setUsers(users.map(u => {
        if (u.uid === userId) {
          return {
            ...u,
            org: updatedOrgs
          };
        }
        return u;
      }));
      
      toast.success('Organization updated successfully');
      setEditingOrg(null);
      setNewOrgId('');
    } catch (error) {
      console.error('Error updating org:', error);
      toast.error('Failed to update organization');
    }
  };

  const handleRemoveOrg = async (userId, orgId) => {
    try {
      const user = users.find(u => u.uid === userId);
      
      if (!user.org) return;
      
      const updatedOrgs = user.org.filter(id => id !== orgId);
      
      // Update in Firestore
      await updateDoc(doc(db, 'users', userId), {
        org: updatedOrgs
      });
      
      // Update local state
      setUsers(users.map(u => {
        if (u.uid === userId) {
          return {
            ...u,
            org: updatedOrgs
          };
        }
        return u;
      }));
      
      toast.success('Organization removed successfully');
    } catch (error) {
      console.error('Error removing org:', error);
      toast.error('Failed to remove organization');
    }
  };

  // Add handler for manager role toggle
  const handleManagerToggle = async (userId, isManager) => {
    try {
      // Update in Firestore
      await updateDoc(doc(db, 'users', userId), {
        'roles.manager': isManager ? 1 : 0
      });
      
      // Update local state
      setUsers(users.map(u => {
        if (u.uid === userId) {
          return {
            ...u,
            roles: {
              ...(u.roles || {}),
              manager: isManager ? 1 : 0
            }
          };
        }
        return u;
      }));
      
      toast.success(`User ${isManager ? 'set as' : 'removed as'} manager`);
    } catch (error) {
      console.error('Error updating manager role:', error);
      toast.error('Failed to update manager role');
    }
  };

  const displayUsers = users;

  const getUserDomain = (email) => {
    const parts = email?.split('@');
    return parts?.length > 1 ? parts[1] : '';
  };

  return (
    <div className="w-full min-h-screen  ">
      <h1 className="text-2xl font-bold text-zinc-100 mb-6">User Management</h1>
      
      {/* Mobile view - filters above content */}
      <div className="block md:hidden mb-6">
        <div className="bg-black/10 rounded-lg shadow p-4 mb-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-300 mb-1">Search Users</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-zinc-500" />
              </div>
              <input
                type="text"
                placeholder="Search by name or email"
                className="pl-10 w-full rounded-md border-zinc-700 bg-black/20 text-zinc-200 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1">Filter by Domain</label>
            <div className="bg-black/30 p-2 rounded-md max-h-40 overflow-y-auto">
              <div className="flex items-center mb-2">
                <input
                  type="checkbox"
                  id="select-all-mobile"
                  className="h-4 w-4 text-sky-500 rounded border-zinc-600 bg-black/10 focus:ring-sky-500"
                  checked={selectedDomains.length === availableDomains.length}
                  onChange={handleSelectAllDomains}
                />
                <label htmlFor="select-all-mobile" className="ml-2 text-sm text-zinc-300">
                  Select All
                </label>
              </div>
              
              {availableDomains.map(domain => (
                <div key={domain} className="flex items-center mb-1">
                  <input
                    type="checkbox"
                    id={`domain-${domain}-mobile`}
                    className="h-4 w-4 text-sky-500 rounded border-zinc-600 bg-black/10 focus:ring-sky-500"
                    checked={selectedDomains.includes(domain)}
                    onChange={() => handleDomainToggle(domain)}
                  />
                  <label htmlFor={`domain-${domain}-mobile`} className="ml-2 text-sm text-zinc-300 truncate">
                    {domain}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Desktop layout - side-by-side */}
      <div className="flex flex-col md:flex-row gap-6">
        {/* Left column - Filters (hidden on mobile) */}
        <div className="hidden md:block md:w-1/4">
          <div className="bg-black/10 rounded-lg shadow p-4 sticky top-4">
            <div className="mb-6">
              <label className="block text-sm font-medium text-zinc-300 mb-1">Search Users</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-zinc-500" />
                </div>
                <input
                  type="text"
                  placeholder="Search by name or email"
                  className="pl-10 w-full rounded-md border-zinc-700 bg-black/20 text-zinc-200 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                  value={searchTerm}
                  onChange={handleSearch}
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Filter by Domain</label>
              <div className="bg-black/30 p-3 rounded-md  overflow-y-auto">
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    id="select-all"
                    className="h-4 w-4 text-sky-500 rounded border-zinc-600 bg-black/10 focus:ring-sky-500"
                    checked={selectedDomains.length === availableDomains.length}
                    onChange={handleSelectAllDomains}
                  />
                  <label htmlFor="select-all" className="ml-2 text-sm text-zinc-300">
                    Select All
                  </label>
                </div>
                
                {availableDomains.map(domain => (
                  <div key={domain} className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      id={`domain-${domain}`}
                      className="h-4 w-4 text-sky-500 rounded border-zinc-600 bg-black/10 focus:ring-sky-500"
                      checked={selectedDomains.includes(domain)}
                      onChange={() => handleDomainToggle(domain)}
                    />
                    <label htmlFor={`domain-${domain}`} className="ml-2 text-sm text-zinc-300 truncate">
                      {domain}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Right column - User Table */}
        <div className="md:w-3/4 w-full">
          <div className="flex justify-end mb-2">
            <span className="text-sm text-zinc-400 mr-4 mb-2">
              Viewing <span className="font-medium text-sky-400">{allUsers.filter(user => {
                // Filter by search term if provided
                const matchesSearch = !searchTerm || 
                  user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  user.email?.toLowerCase().includes(searchTerm.toLowerCase());
                
                // Filter by domain
                const emailParts = user.email?.split('@');
                const domain = emailParts?.length > 1 ? emailParts[1] : null;
                const matchesDomain = domain && selectedDomains.includes(domain);
                
                return matchesSearch && matchesDomain;
              }).length}</span> of <span className="font-medium text-zinc-300">{allUsers.length}</span> users
            </span>
          </div>
          <div className="bg-black/10 rounded-lg shadow overflow-hidden">
            {loading && !users.length ? (
              <div className="flex justify-center items-center p-12">
                <span className="text-zinc-300">loading...</span>
              </div>
            ) : (
              <>
                {/* User count info moved above */}
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-zinc-800">
                    <thead className="bg-black/30">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                          User
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                          Email
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                          Organizations
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                          Roles
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-black/10 divide-y divide-zinc-800">
                      {users.map(user => (
                        <tr key={user.uid} className="hover:bg-black/30 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-10 w-10 flex-shrink-0">
                                {user.photoURL ? (
                                  <img 
                                    className="h-10 w-10 rounded-full object-cover" 
                                    src={user.photoURL} 
                                    alt={user.displayName || 'User'} 
                                    referrerPolicy="no-referrer"
                                  />
                                ) : (
                                  <div className="h-10 w-10 rounded-full bg-sky-900 flex items-center justify-center">
                                    <span className="text-sky-300 font-medium">
                                      {user.displayName 
                                        ? user.displayName.charAt(0).toUpperCase() 
                                        : user.email.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-zinc-200">
                                  {user.displayName || 'No name'}
                                </div>
                                <div className="text-xs text-zinc-500">
                                  {getUserDomain(user.email)}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-zinc-300">{user.email}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-zinc-300">
                              {editingOrg === user.uid ? (
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="number"
                                    className="w-20 rounded-md border-zinc-700 bg-black/20 text-zinc-200 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                                    value={newOrgId}
                                    onChange={(e) => setNewOrgId(e.target.value)}
                                    placeholder="Org ID"
                                  />
                                  <button
                                    onClick={() => handleAddOrg(user.uid)}
                                    className="px-2 py-1 text-xs bg-sky-500 text-zinc-100 rounded hover:bg-sky-400 transition-colors"
                                  >
                                    Add
                                  </button>
                                  <button
                                    onClick={() => setEditingOrg(null)}
                                    className="px-2 py-1 text-xs bg-zinc-700 text-zinc-200 rounded hover:bg-zinc-600 transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div>
                                  {user.org && user.org.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                      {user.org.map(orgId => (
                                        <span key={orgId} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-sky-900 text-sky-200">
                                          {orgId}
                                          <button 
                                            onClick={() => handleRemoveOrg(user.uid, orgId)}
                                            className="ml-1.5 text-sky-300 hover:text-sky-100"
                                          >
                                            ×
                                          </button>
                                        </span>
                                      ))}
                                      <button
                                        onClick={() => startEditingOrg(user.uid)}
                                        className="text-sky-400 hover:text-sky-300 text-xs"
                                      >
                                        + Add
                                      </button>
                                    </div>
                                  ) : (
                                    <div>
                                      <span className="text-zinc-500 text-xs italic">No organizations</span>
                                      <button
                                        onClick={() => startEditingOrg(user.uid)}
                                        className="ml-2 text-sky-400 hover:text-sky-300 text-xs"
                                      >
                                        + Add
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id={`manager-${user.uid}`}
                                className="h-4 w-4 text-sky-500 rounded border-zinc-600 bg-black/10 focus:ring-sky-500"
                                checked={user.roles?.manager === 1}
                                onChange={(e) => handleManagerToggle(user.uid, e.target.checked)}
                              />
                              <label htmlFor={`manager-${user.uid}`} className="ml-2 text-sm text-zinc-300">
                                Manager
                              </label>
                            </div>
                          </td>
                        </tr>
                      ))}
                      
                      {users.length === 0 && (
                        <tr>
                          <td colSpan="3" className="px-6 py-10 text-center text-zinc-500">
                            No users match your search filters
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination controls */}
                <div className="bg-black/30 px-4 py-3 flex items-center justify-between border-t border-zinc-800 sm:px-6">
                  <div className="flex-1 flex justify-between items-center">
                    <button
                      onClick={handlePrevPage}
                      disabled={page === 1}
                      className={`relative inline-flex items-center px-4 py-2 border border-zinc-700 text-sm font-medium rounded-md ${
                        page === 1 
                          ? 'bg-black/40 text-zinc-500 cursor-not-allowed' 
                          : 'bg-black/20 text-zinc-300 hover:bg-black/30'
                      }`}
                    >
                      Previous
                    </button>
                    <span className="text-sm text-zinc-400">
                      Page {page} of {totalPages}
                    </span>
                    <button
                      onClick={handleNextPage}
                      disabled={page >= totalPages}
                      className={`relative inline-flex items-center px-4 py-2 border border-zinc-700 text-sm font-medium rounded-md ${
                        page >= totalPages
                          ? 'bg-black/40 text-zinc-500 cursor-not-allowed'
                          : 'bg-black/20 text-zinc-300 hover:bg-black/30'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
